import { Vector3, type PerspectiveCamera } from "three";

export interface AimInput {
  aimX: number; // unit vector on xz plane
  aimZ: number;
  power: number; // 0..1
}

export interface AimEventHandlers {
  onAimStart(): void;
  onAimMove(input: AimInput): void;
  onAimCancel(): void;
  onShoot(input: AimInput): void;
  getBallPosition(): Vector3;
  isInteractive(): boolean;
}

// Power is measured in SCREEN PIXELS of drag, not world units — predictable on
// any device and independent of camera zoom/angle.
const MIN_PIXELS = 8; // deadzone so a tap does nothing
const MAX_PIXELS_DESKTOP = 180;
const MAX_PIXELS_MOBILE = 130;

export function installAim(
  canvas: HTMLElement,
  camera: PerspectiveCamera,
  handlers: AimEventHandlers,
): () => void {
  const isMobile = matchMedia("(pointer: coarse)").matches;
  const maxPixels = isMobile ? MAX_PIXELS_MOBILE : MAX_PIXELS_DESKTOP;

  let pointerId: number | null = null;
  // Initial screen (client coords). Aim is pure screen-delta; we no longer
  // raycast to the xz plane, so the initial drag position doesn't matter.
  let screenStartX = 0;
  let screenStartY = 0;

  const computeAim = (
    clientX: number,
    clientY: number,
  ): AimInput => {
    const pdx = clientX - screenStartX;
    const pdy = clientY - screenStartY;
    const pixels = Math.hypot(pdx, pdy);
    if (pixels < MIN_PIXELS) return { aimX: 0, aimZ: 0, power: 0 };

    // Pure screen-delta aiming. Drag down (pdy > 0) = shoot forward.
    const fallback = screenDeltaToWorldDir(pdx, pdy, camera);
    const power = Math.min(1, (pixels - MIN_PIXELS) / (maxPixels - MIN_PIXELS));
    return { aimX: fallback.x, aimZ: fallback.z, power };
  };

  const onDown = (e: PointerEvent) => {
    if (!handlers.isInteractive()) return;
    if (pointerId !== null) return;
    pointerId = e.pointerId;
    screenStartX = e.clientX;
    screenStartY = e.clientY;
    canvas.setPointerCapture(e.pointerId);
    handlers.onAimStart();
    e.preventDefault();
  };

  const onMove = (e: PointerEvent) => {
    if (e.pointerId !== pointerId) return;
    const aim = computeAim(e.clientX, e.clientY);
    handlers.onAimMove(aim);
    e.preventDefault();
  };

  const onUp = (e: PointerEvent) => {
    if (e.pointerId !== pointerId) return;
    const aim = computeAim(e.clientX, e.clientY);
    canvas.releasePointerCapture(e.pointerId);
    pointerId = null;
    if (aim.power > 0.05) {
      handlers.onShoot(aim);
    } else {
      handlers.onAimCancel();
    }
    e.preventDefault();
  };

  const onCancel = (e: PointerEvent) => {
    if (e.pointerId !== pointerId) return;
    canvas.releasePointerCapture(e.pointerId);
    pointerId = null;
    handlers.onAimCancel();
  };

  canvas.addEventListener("pointerdown", onDown, { passive: false });
  canvas.addEventListener("pointermove", onMove, { passive: false });
  canvas.addEventListener("pointerup", onUp, { passive: false });
  canvas.addEventListener("pointercancel", onCancel);
  canvas.addEventListener("contextmenu", (e) => e.preventDefault());

  return () => {
    canvas.removeEventListener("pointerdown", onDown);
    canvas.removeEventListener("pointermove", onMove);
    canvas.removeEventListener("pointerup", onUp);
    canvas.removeEventListener("pointercancel", onCancel);
  };
}

// Fallback: when the raycast doesn't land on the xz plane (e.g. the pointer
// is looking at the skydome) we synthesize a direction from screen delta
// projected via the camera's basis. Screen +x → camera right. Screen +y → away
// from camera in the plane (negative forward since screen y is down).
function screenDeltaToWorldDir(
  dx: number,
  dy: number,
  camera: PerspectiveCamera,
): { x: number; z: number } {
  const right = new Vector3();
  const forward = new Vector3();
  camera.getWorldDirection(forward);
  right.crossVectors(forward, new Vector3(0, 1, 0)).normalize();
  // Project onto xz plane.
  forward.y = 0;
  forward.normalize();
  // Pull direction in world = -(dx * right + dy * -forward) then invert to fire.
  const wx = -dx * right.x + dy * forward.x;
  const wz = -dx * right.z + dy * forward.z;
  const len = Math.hypot(wx, wz) || 1;
  return { x: wx / len, z: wz / len };
}
